import { useCallback } from 'react';

import { useSubmitCareerExerciseMutation, CareerLessonDetailDocument } from 'core/graphql';

export interface IExerciseApplyReport {
  writtenTargets: string[];
  skippedTargets: string[];
  profileCompleteness: number;
}

export interface IExerciseSubmitResult {
  success: boolean;
  /** Медали и коды, выданные ИМЕННО этой сдачей. Пусто — норма. */
  grantedTitles: string[];
  promoCodes: string[];
  report: IExerciseApplyReport | null;
  lessonsCompleted: number;
  lessonsTotal: number;
  errorMessage: string;
  errorField: string;
}

/**
 * ТЗ 0039 — сдача анкеты урока.
 *
 * После успеха перечитываем ИМЕННО этот урок: сдача засчитывает его и
 * открывает следующий, поэтому страница обязана увидеть новое состояние
 * замка. Перечитываем по документу с ТЕМИ ЖЕ переменными — рефетч по имени
 * операции промахивается молча, если запрос где-то ещё вызван с другими
 * аргументами (шрам ТЗ 0033).
 */
export const useSubmitCareerExercise = (lessonId?: string) => {
  const [submit, { loading }] = useSubmitCareerExerciseMutation();

  const submitExercise = useCallback(
    async (answers: { fieldId: string; value: string }[]): Promise<IExerciseSubmitResult> => {
      const { data } = await submit({
        variables: { lessonId: lessonId ?? '', answers },
        refetchQueries: lessonId
          ? [{ query: CareerLessonDetailDocument, variables: { id: lessonId } }]
          : [],
        awaitRefetchQueries: true,
      });
      const payload = data?.submitCareerLessonExercise;
      const error = (payload?.customErrors ?? []).find(Boolean);

      return {
        success: Boolean(payload?.success),
        grantedTitles: [...(payload?.reward?.grantedTitles ?? [])],
        promoCodes: [...(payload?.reward?.promoCodes ?? [])],
        report: payload?.applyReport
          ? {
              writtenTargets: [...payload.applyReport.writtenTargets],
              skippedTargets: [...payload.applyReport.skippedTargets],
              profileCompleteness: payload.applyReport.profileCompleteness,
            }
          : null,
        lessonsCompleted: payload?.lessonsCompleted ?? 0,
        lessonsTotal: payload?.lessonsTotal ?? 0,
        errorMessage: error?.message ?? '',
        errorField: error?.field ?? '',
      };
    },
    [submit, lessonId],
  );

  return { submitExercise, submitting: loading };
};
